import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId, scrollToTop } from "../lib/scroll";

const links = [
  { label: "Products", href: "#products" },
  { label: "SMART AI", href: "#featured-project" },
  { label: "How We Build", href: "#how-we-build" },
  { label: "About", href: "#about" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-[100] transition-colors duration-300 ${
        scrolled
          ? "border-b border-hairline bg-white/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6 md:h-[72px] lg:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => scrollToTop()}
          className="flex items-center"
          aria-label="MisiDigi Lab — home"
        >
          <img
            src="/misidigi-logo.png"
            alt="MisiDigi Lab"
            className="h-[26px] w-auto md:h-[32px]"
          />
        </button>

        <div className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="relative text-[14px] font-medium text-slatetx transition-colors hover:text-ink"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta"
            onClick={() => go("#products")}
            className="rounded-full bg-ink px-5 py-2 text-[13px] font-semibold text-white transition-all hover:bg-steel"
          >
            Explore Products
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-hairline bg-surface text-ink md:hidden"
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-hairline bg-canvas/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {links.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                  onClick={() => go(l.href)}
                  className="py-3 text-left text-[16px] font-medium text-ink"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#products")}
                className="mt-2 rounded-full bg-ink px-5 py-3 text-center text-[15px] font-semibold text-white"
              >
                Explore Products
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
